<template>
  <div class="wrapper msc-legal-page">
    <header class="msc-legal-hero">
      <div
        class="msc-legal-hero__media"
        aria-hidden="true"
        style="background-image: url('/img/pexels-photo-716431.jpeg')"
      ></div>
      <div class="msc-legal-hero__scrim" aria-hidden="true"></div>
      <div class="msc-legal-hero__content">
        <p class="msc-legal-hero__eyebrow">License</p>
        <h1 class="msc-legal-hero__title">Terms of Use</h1>
      </div>
    </header>

    <section class="msc-legal-body">
      <div class="container msc-legal-body__container">
        <p class="msc-legal-meta">Effective as of: July 24, 2026</p>

        <p class="msc-legal-intro">
          Welcome to mySchool.cafe, an online service offered by Newcare International, LLC and its
          affiliates (“Newcare,” “we,” “us,” or “our”). These Terms of Use explain our obligations as
          provider and yours as a user. By using the Service, you agree to these terms without
          modification. If you do not agree, please discontinue use.
        </p>
        <p class="msc-legal-plain">
          <strong>In plain terms</strong>
          Please read this so you know the rules. Using mySchool.cafe means you accept them. If you
          can’t agree, you shouldn’t use the Service.
        </p>

        <section class="msc-legal-section">
          <h2>A. Definitions</h2>
          <ul>
            <li>
              <strong>Agreement</strong> means these Terms of Use.
            </li>
            <li>
              <strong>Applicable law</strong> means the laws of the State of Mississippi and applicable
              United States law.
            </li>
            <li>
              <strong>Site</strong> means mySchool.cafe and related Newcare pages for this Service, not
              third-party websites that may be linked.
            </li>
            <li>
              <strong>Service</strong> means the school meal ordering and related online features offered
              through mySchool.cafe.
            </li>
            <li>
              <strong>You</strong> means any person who visits the Site or uses the Service, including
              parents, guardians, and authorized school users.
            </li>
          </ul>
        </section>

        <section class="msc-legal-section">
          <h2>B. License to use the Service</h2>
          <p>
            Newcare grants you a non-exclusive, non-transferable, limited license to use mySchool.cafe
            and related resources in accordance with these Terms. We (and our licensors) retain all
            ownership of the Service, software, designs, and content we provide. You retain ownership of
            the information you submit. You grant Newcare a limited right to use that information as
            needed to operate the Service for you and your school.
          </p>
          <p class="msc-legal-plain">
            <strong>In plain terms</strong>
            You’re welcome to use the Service. You don’t own our software; we don’t own your data.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>C. Your account</h2>
          <p>
            If you register, you agree to provide accurate information and to keep your login credentials
            confidential. You are responsible for activity under your account. Notify us promptly if you
            believe your account has been used without authorization.
          </p>
          <p>
            Registration and use are also subject to our
            <router-link to="/privacy">Privacy Policy</router-link>, which is part of this Agreement.
          </p>
          <p class="msc-legal-plain">
            <strong>In plain terms</strong>
            Keep your password safe. You’re responsible for what happens in your account.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>D. Acceptable use</h2>
          <p>You agree to use the Service only for its intended school meal–related purposes and to:</p>
          <ul>
            <li>Comply with applicable law and your school’s rules for meal programs</li>
            <li>Not attempt to disrupt, hack, or overload the Service</li>
            <li>Not access another user’s account without authorization</li>
            <li>Not upload malware or unlawful, abusive, or infringing content</li>
            <li>Not copy, sell, reverse engineer, or redistribute the Service except as we expressly allow</li>
          </ul>
          <p>
            You are responsible for the accuracy of information you enter (including student and order
            details). Newcare is not responsible for errors that result from incorrect information you
            provide.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>E. Schools and third parties</h2>
          <p>
            mySchool.cafe is provided in connection with participating schools. School staff may use
            order and account information as needed to operate their meal program. The Service may also
            rely on hosting or other technical providers. Links to other sites are for convenience; we
            are not responsible for third-party content or practices.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>F. Electronic communications</h2>
          <p>
            You consent to receive Service-related communications electronically (for example, account
            activation or password reset) at the email associated with your account. You may contact
            support if you need help with your account. We do not use the Service to send promotional
            marketing messages.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>G. Changes to the Service or these terms</h2>
          <p>
            We may update the Service or these Terms from time to time. Material changes will be posted
            on the Site. Continued use after changes are posted constitutes acceptance of the updated
            Terms. Your sole remedy if you do not agree is to stop using the Service.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>H. Disclaimer and limitation of liability</h2>
          <p>
            THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW,
            NEWCARE DISCLAIMS WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
            NON-INFRINGEMENT, AND DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE,
            OR COMPLETELY SECURE.
          </p>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEWCARE AND ITS AFFILIATES, SUPPLIERS, AND
            CONTRACTORS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
            DAMAGES, OR FOR LOSS OF DATA, PROFITS, OR BUSINESS, ARISING FROM YOUR USE OF THE SERVICE,
            EVEN IF ADVISED OF THE POSSIBILITY. YOUR SOLE AND EXCLUSIVE REMEDY FOR DISSATISFACTION WITH
            THE SERVICE IS TO STOP USING IT.
          </p>
          <p class="msc-legal-plain">
            <strong>In plain terms</strong>
            We work hard to keep things running, but the Service isn’t perfect. Liability is limited;
            if it’s not working for you, stop using it.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>I. Termination</h2>
          <p>
            We may suspend or end access if you breach these Terms, if we suspect fraud or compromise,
            or as needed to protect the Service or a school’s program. You may stop using the Service at
            any time. Upon termination, your right to use the Service ends.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>J. Disclosure of information</h2>
          <p>
            You own the personal and household information you provide. We may disclose information when
            required for law enforcement, legal or regulatory requirements, court order, or to our
            counsel in connection with legal matters. See our
            <router-link to="/privacy">Privacy Policy</router-link> for more detail.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>K. Governing law</h2>
          <p>
            This Agreement is governed by the laws of the State of Mississippi and applicable United
            States law, without regard to conflict-of-law rules. Courts in Mississippi have exclusive
            jurisdiction, except where prohibited by law.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>L. Other</h2>
          <p>
            If any part of these Terms is found unenforceable, the rest remains in effect. These Terms,
            together with the Privacy Policy, are the entire agreement regarding use of the Service.
            mySchool.cafe and related marks are property of Newcare International, LLC. The English
            language version controls.
          </p>
        </section>

        <section class="msc-legal-section">
          <h2>Contact</h2>
          <p>
            Questions about these Terms may be sent to
            <a href="mailto:support@newcare.international">support@newcare.international</a>
            or visit
            <a href="https://www.newcare.international" target="_blank" rel="noopener noreferrer">
              newcare.international
            </a>.
          </p>
        </section>
      </div>
    </section>
  </div>
</template>

<script>
  export default {
    name: 'license-terms',
    bodyClass: 'msc-legal-page',
  };
</script>
